import { memo, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoginState, getAccountState } from "../../redux/slices/accountSlice";
import { getEventState } from "../../redux/slices/eventSlice";
import { getUserRoleTicketFromUserTickets } from "../../utils/eventUtils/userTicketUtils";
import LoadingScreen from "../LoadingScreen";
import { useLiveUpdatingEvent } from "../../hooks/useLiveUpdatingEvent";
import { useEventSelector } from "../../hooks/useParametricSelector";
import { useNavigate } from "../../hooks/useNavigate";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import { DataLoaders } from "../../redux/slices/dataSlice";
import { NewProductNavigationId } from "../../navigation/AppParamList";
import ProductDashboard from "./ProductDashboard";
import EventDashboard from "../Event/EventDashboard";
import { isProductSpecificType } from "@markit/common.utils";

// Used to determine which dashboard to show depending on the event type
const ProductDashboardDecider = memo(function ProductDashboardDeciderFn() {
  const { account } = useSelector(getAccountState);
  const { events } = useSelector(getEventState);
  const { accountData, loggedIn, appInitialized } = account;
  const { tickets: userTickets } = events;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const eventId = useEventSelector();
  // use event and never eventId because we can also get eventId from locationState
  const {
    liveEvent: event,
    isLiveEventError,
    loaded: eventLoaded,
  } = useLiveUpdatingEvent({
    initialEventId: eventId,
  });

  const [loading, setLoading] = useState(true);

  const userTicket = useMemo(
    () =>
      event
        ? getUserRoleTicketFromUserTickets(
            event.id,
            accountData.uid,
            userTickets
          )
        : undefined,
    [accountData.uid, event, userTickets]
  );

  // TODO: Create a custom hook wrapper that can load all the required data, so we don't need to pass it as props throughout
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (
      !eventLoaded ||
      !event ||
      (loggedIn === LoginState.LOGGED_IN && !appInitialized)
    ) {
      return;
    }

    // Needs to be ran here fo
    // const userTicketData = getUserRoleTicketFromUserTickets(
    //   event.id,
    //   accountData.uid,
    //   userTickets
    // );

    /*
     * userTicket = undefined -> if user doesn't have a ticket
     * userTicket.customTicketId !== "" -> if user has a ticket but is not an organizer
     */
    // redirect if not an admin or organizer for the event
    if (
      !accountData.isAdmin &&
      (!userTicket ||
        userTicket.customTicketId !== "" ||
        loggedIn === LoginState.LOGGED_OUT)
    ) {
      dispatch(DataLoaders.event(event.id));
      navigate("/e/" + event.id);
    }

    if (event.isDraft) {
      dispatch(DataLoaders.event(event.id));
      navigate(NewProductNavigationId.EVENT, {
        state: { eventId: event.id },
      });
    }

    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appInitialized, eventLoaded]);

  if (!event || loading || !appInitialized) {
    return (
      <LoadingScreen isLoadingPage={!event || loading || !appInitialized} />
    );
  }

  if (isLiveEventError) {
    return <LoadingScreen error />;
  }

  if (isProductSpecificType(event.eventType)) {
    return <ProductDashboard product={event} userTicket={userTicket} />;
  }

  return <EventDashboard event={event} userTicket={userTicket} />;
});

export default ProductDashboardDecider;
